import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ROUTES } from './constants/routes';

import { HelmetProvider } from 'react-helmet-async';
import NavigationBar from './components/common/Navbar';
import Footer from './components/common/Footer';
import ErrorBoundary from './components/common/ErrorBoundary';
import ScrollToTop from './components/common/ScrollToTop';
import ClodeAssistant from './components/common/ClodeAssistant';
import LoadingScreen from './components/common/LoadingScreen';
import DigitalMarketingApp from './digital/DigitalMarketingApp';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';


// Lazy load public pages
const Home = lazy(() => import('./pages/Home'));
const CloudServices = lazy(() => import('./pages/Services/CloudServices'));
const CyberSecurity = lazy(() => import('./pages/Services/CyberSecurity'));
const ManagedServices = lazy(() => import('./pages/Services/ManagedServices'));
const DevOpsAutomation = lazy(() => import('./pages/Services/DevOpsAutomation'));
const DataAnalytics = lazy(() => import('./pages/Services/DataAnalytics'));
const BackupRecovery = lazy(() => import('./pages/Services/BackupRecovery'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Resources = lazy(() => import('./pages/Resources'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const EnterpriseLearning = lazy(() => import('./pages/Services/EnterpriseLearning'));
const CloudMigration = lazy(() => import('./pages/Services/CloudMigration'));
const Finops = lazy(() => import('./pages/Services/Finops'));
const GenAiReadiness = lazy(() => import('./pages/Services/GenAiReadiness'));
const LeadCapture = lazy(() => import('./pages/LeadCapture'));
const AwsConsulting = lazy(() => import('./pages/Services/AwsConsulting'));
const AzureConsulting = lazy(() => import('./pages/Services/AzureConsulting'));
const Login = lazy(() => import('./pages/Login'));
const BlogManager = lazy(() => import('./pages/Portal/BlogManager').then(m=>({default:m.BlogManager})));
const SupportDesk = lazy(() => import('./pages/Portal/SupportDesk').then(m=>({default:m.SupportDesk})));
const CameraEvents = lazy(() => import('./pages/Portal/CameraEvents').then(m=>({default:m.CameraEvents})));
const OperationsHealth = lazy(() => import('./pages/Portal/OperationsHealth').then(m=>({default:m.OperationsHealth})));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

const Startups = lazy(() => import('./pages/Solutions/Startups'));
const Noida = lazy(() => import('./pages/Locations/Noida'));
const Lucknow = lazy(() => import('./pages/Locations/Lucknow'));
const CloudMigrationCalculator = lazy(() => import('./pages/Resources/CloudMigrationCalculator'));
const SmbMsme = lazy(() => import('./pages/Solutions/SmbMsme'));
const Enterprises = lazy(() => import('./pages/Solutions/Enterprises'));

const PortalLayout = lazy(() =>
  import('./components/portal/PortalLayout').then((module) => ({ default: module.PortalLayout }))
);
const ExecutiveDashboard = lazy(() =>
  import('./pages/Portal/ExecutiveDashboard').then((module) => ({ default: module.ExecutiveDashboard }))
);
const MigrationTracker = lazy(() =>
  import('./pages/Portal/MigrationTracker').then((module) => ({ default: module.MigrationTracker }))
);
const ComplianceCenter = lazy(() =>
  import('./pages/Portal/ComplianceCenter').then((module) => ({ default: module.ComplianceCenter }))
);
const CloudOptimization = lazy(() =>
  import('./pages/Portal/CloudOptimization').then((module) => ({ default: module.CloudOptimization }))
);
const ManagedServicesPortal = lazy(() =>
  import('./pages/Portal/ManagedServicesPortal').then((module) => ({ default: module.ManagedServicesPortal }))
);
const LeadOperations = lazy(() =>
  import('./pages/Portal/LeadOperations').then((module) => ({ default: module.LeadOperations }))
);
const ExpertAdvisoryCenter = lazy(() =>
  import('./pages/Portal/ExpertAdvisoryCenter').then((module) => ({ default: module.ExpertAdvisoryCenter }))
);
const CloudStrategy = lazy(() =>
  import('./pages/Portal/CloudStrategy').then((module) => ({ default: module.CloudStrategy }))
);
const PortalBackupRecovery = lazy(() =>
  import('./pages/Portal/BackupRecovery').then((module) => ({ default: module.BackupRecovery }))
);

function App() {
  const hostname = window.location.hostname.toLowerCase();
  const isDigitalMarketingHost =
    hostname === 'digitalmarketing.cloudyatra.co.in' || hostname === 'digitalmarketing.localhost';

  if (isDigitalMarketingHost) {
    return (
      <HelmetProvider>
        <ErrorBoundary>
          <DigitalMarketingApp />
        </ErrorBoundary>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider><AuthProvider>
      <ErrorBoundary>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* Enterprise Portal Routes - Modern Design System */}
              <Route path="/login" element={<Login />} />
              <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
                <Route index element={<ExecutiveDashboard />} />
                <Route path="migration" element={<MigrationTracker />} />
                <Route path="compliance" element={<ComplianceCenter />} />
                <Route path="optimization" element={<CloudOptimization />} />
                <Route path="managed-services" element={<ManagedServicesPortal />} />
                <Route path="leads" element={<LeadOperations />} />
                <Route path="blog" element={<BlogManager />} />
                <Route path="support" element={<SupportDesk />} />
                <Route path="camera-events" element={<CameraEvents />} />
                <Route path="operations" element={<OperationsHealth />} />
                <Route path="advisory" element={<ExpertAdvisoryCenter />} />
                <Route path="strategy" element={<CloudStrategy />} />
                <Route path="backup" element={<PortalBackupRecovery />} />
              </Route>

              {/* Public Website Routes - Legacy Bootstrap System */}
              <Route
                path="*"
                element={
                  <div className="flex flex-col min-h-screen bg-[#020712]">
                    <NavigationBar />
                    <a href="#main-content" className="skip-link">
                      Skip to main content
                    </a>
                    <main id="main-content" className="flex-grow w-full" tabIndex={-1}>
                      <div className="w-full p-0 m-0">
                        <ErrorBoundary>
                          <Routes>
                            <Route path={ROUTES.HOME} element={<Home />} />
                            <Route path={ROUTES.SERVICES.OVERVIEW} element={<CloudServices />} />
                            <Route path={ROUTES.SERVICES.CLOUD_SERVICES} element={<CloudServices />} />
                            <Route path={ROUTES.SERVICES.CLOUD_MIGRATION} element={<CloudMigration />} />
                            <Route path={ROUTES.SERVICES.FINOPS} element={<Finops />} />
                            <Route path={ROUTES.SERVICES.SECURITY_GOVERNANCE} element={<CyberSecurity />} />
                            <Route path={ROUTES.SERVICES.GENAI_READINESS} element={<GenAiReadiness />} />
                            <Route path={ROUTES.SERVICES.CYBERSECURITY} element={<CyberSecurity />} />
                            <Route path={ROUTES.SERVICES.MANAGED_SERVICES} element={<ManagedServices />} />
                            <Route path={ROUTES.SERVICES.DEVOPS} element={<DevOpsAutomation />} />
                            <Route path={ROUTES.SERVICES.DATA_ANALYTICS} element={<DataAnalytics />} />
                            <Route path={ROUTES.SERVICES.BACKUP} element={<BackupRecovery />} />
                            <Route path={ROUTES.SERVICES.LEARNING} element={<EnterpriseLearning />} />
                            <Route path={ROUTES.SERVICES.AWS} element={<AwsConsulting />} />
                            <Route path={ROUTES.SERVICES.AZURE} element={<AzureConsulting />} />
                            <Route path={ROUTES.LOCATIONS.NOIDA} element={<Noida />} />
                            <Route path={ROUTES.LOCATIONS.LUCKNOW} element={<Lucknow />} />
                            <Route path={ROUTES.CALCULATOR} element={<CloudMigrationCalculator />} />
                            <Route path={ROUTES.QR_CAPTURE} element={<LeadCapture />} />
                            <Route path={ROUTES.ABOUT} element={<About />} />
                            <Route path={ROUTES.CONTACT} element={<Contact />} />
                            <Route path={ROUTES.RESOURCES} element={<Resources />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path={ROUTES.THANK_YOU} element={<ThankYou />} />
                            <Route path={ROUTES.SOLUTIONS.STARTUPS} element={<Startups />} />
                            <Route path={ROUTES.SOLUTIONS.SMB_MSME} element={<SmbMsme />} />
                            <Route path={ROUTES.SOLUTIONS.ENTERPRISES} element={<Enterprises />} />
                          </Routes>
                        </ErrorBoundary>
                      </div>
                    </main>
                    <Footer />
                    <ClodeAssistant />
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </Router>
      </ErrorBoundary>
    </AuthProvider></HelmetProvider>
  );
}

export default App;
